import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../apiBase';

export const OFFICE_POLL_MS = 30000;

export function useOfficeRead(url, identity, enabled = true) {
  const key = `${identity || ''}:${url}`;
  const [result, setResult] = useState({});
  const refreshRef = useRef(() => {});
  useEffect(() => {
    if (!enabled || !identity) return undefined;
    let stopped = false;
    let pending = false;
    let controller;
    const read = async () => {
      if (stopped || pending || document.visibilityState === 'hidden') return;
      pending = true;
      controller = new AbortController();
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api${url}`, { withCredentials: true, signal: controller.signal, timeout: 15000 });
        if (!stopped) setResult({ key, data, error: false });
      } catch (error) {
        if (!stopped && !controller.signal.aborted) setResult(old => ({ key, data: old.key === key ? old.data : null, error: true }));
      } finally { pending = false; }
    };
    refreshRef.current = read;
    read();
    const timer = setInterval(read, OFFICE_POLL_MS);
    document.addEventListener('visibilitychange', read);
    return () => { stopped = true; controller?.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', read); refreshRef.current = () => {}; };
  }, [key, url, identity, enabled]);
  const current = enabled && result.key === key ? result : {};
  const refresh = useCallback(() => refreshRef.current(), []);
  return { data: current.data, error: !!current.error, loading: !current.data && !current.error, refresh };
}

export function useOfficeHeartbeat(user, impersonating) {
  const uid = user?.id;
  const role = user?.role;
  useEffect(() => {
    if (!uid || impersonating) return undefined;
    let pending = false;
    let stopped = false;
    let controller;
    const ping = async () => {
      if (stopped || pending || document.visibilityState === 'hidden') return;
      pending = true; controller = new AbortController();
      try { await axios.post(`${API_BASE_URL}/api/auth/ping`, {}, { withCredentials: true, signal: controller.signal, timeout: 15000 }); }
      catch { /* Presence expires server-side; never invent an online signal. */ }
      finally { pending = false; }
    };
    ping();
    const timer = setInterval(ping, role === 'reseller' ? OFFICE_POLL_MS : 120000);
    document.addEventListener('visibilitychange', ping);
    return () => { stopped = true; controller?.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', ping); };
  }, [uid, role, impersonating]);
}
